<template>
  <div v-if="visible" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
    @click.self="closeModal">
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-lg w-full max-w-4xl relative">
      <!-- Header -->
      <header class="flex justify-between items-center px-6 py-4 bg-primary-700 text-white rounded-t-lg">
        <h2 class="text-lg font-semibold truncate" :title="courseTitle">
          Course: {{ truncatedCourseTitle }}
        </h2>
        <button @click="closeModal"
          class="text-white hover:bg-secondary-600 p-1 rounded-full focus:outline-none focus:ring-2 focus:ring-gray-400"
          aria-label="Close">
          <svg class="w-6 h-6 text-white dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"
            width="24" height="24" fill="none" viewBox="0 0 24 24">
            <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M6 18 17.94 6M18 18 6.06 6" />
          </svg>
        </button>
      </header>

      <!-- Content -->
      <div class="p-6 max-h-[70vh] overflow-y-auto custom-scrollbar">
        <!-- Tabs -->
        <div class="flex space-x-4 border-b mb-6">
          <button @click="activeTab = 'unit'" :class="tabButtonClass('unit')">
            Unit Progress
          </button>
          <button @click="activeTab = 'test'" :class="tabButtonClass('test')">
            Test Responses
          </button>
        </div>

        <!-- Unit Progress Tab -->
        <div v-if="activeTab === 'unit'">
          <div v-if="Object.keys(filteredUnits).length > 0">
            <div v-for="(units, typeKey) in filteredUnits" :key="typeKey" class="mb-6">
              <h3 class="font-semibold text-lg text-gray-800 dark:text-gray-300 capitalize mb-4">
                {{ typeKey.replace('_', ' ') }}
              </h3>
              <div class="overflow-x-auto">
                <table class="w-full table-auto border-collapse bg-gray-50 dark:bg-gray-900 rounded-lg shadow">
                  <thead class="bg-gray-100 dark:bg-gray-700">
                    <tr>
                      <th class="px-4 py-2 text-left text-sm font-semibold text-gray-600 dark:text-gray-300 w-2/3">Title
                      </th>
                      <th class="px-4 py-2 text-center text-sm font-semibold text-gray-600 dark:text-gray-300 w-1/6">
                        Completed</th>
                      <th class="px-4 py-2 text-center text-sm font-semibold text-gray-600 dark:text-gray-300 w-1/6">
                        Time Spent</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="unit in units" :key="unit.id"
                      class="border-b border-gray-200 dark:border-gray-700 last:border-0 hover:bg-gray-100 dark:hover:bg-gray-800">
                      <td class="px-4 py-2 text-gray-700 dark:text-gray-300 truncate" :title="unit.title">
                        {{ truncateText(unit.title, 50) }}
                      </td>
                      <td class="px-4 py-2 text-center">
                        <span class="px-2 py-1 rounded-full text-xs font-medium"
                          :class="unit.is_complete ? 'bg-green-100 text-green-600 dark:bg-green-700 dark:text-green-200' : 'bg-red-100 text-red-600 dark:bg-red-700 dark:text-red-200'">
                          {{ unit.is_complete ? 'Yes' : 'No' }}
                        </span>
                      </td>
                      <td class="px-4 py-2 text-center text-gray-700 dark:text-gray-300">
                        {{ formatTime(unit.time_spent) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div v-else class="text-center py-10 text-gray-600 dark:text-gray-300">
            <p>No units found for this course.</p>
          </div>
        </div>



        <!-- Test Progress Tab -->
        <div v-if="activeTab === 'test'">
          <div v-if="flattenedTestMap.length > 0">
            <div v-for="(unit, uIndex) in flattenedTestMap" :key="uIndex" class="mb-6">
              <h3 class="text-lg font-semibold mb-3 text-primary-700 dark:text-primary-300">
                {{ unit.unitTitle }}
              </h3>

              <div v-for="(entry, tIndex) in unit.tests" :key="tIndex"
                class="border rounded-md mb-4 bg-white dark:bg-gray-900 shadow-sm">
                <div class="px-4 py-3">
                  <p class="text-sm font-medium text-gray-800 dark:text-gray-200 mb-2">
                    {{ entry.test.question }}
                  </p>

                  <!-- MULTIPLE CHOICE -->
                  <template v-if="entry.test.type === 'Multiple Choice'">
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p class="font-semibold text-gray-500 dark:text-gray-400 mb-1">Correct:</p>
                        <ul class="pl-1 space-y-1">
                          <li v-for="(opt, i) in filteredCorrectOptions(entry.test.options)" :key="i"
                            class="flex items-center space-x-2 text-gray-700 dark:text-gray-300">
                            <svg class="w-5 h-5 text-green-500 flex-shrink-0" xmlns="http://www.w3.org/2000/svg"
                              fill="currentColor" viewBox="0 0 24 24" style="min-width: 1.25rem; min-height: 1.25rem;">
                              <path fill-rule="evenodd" clip-rule="evenodd"
                                d="M2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10S2 17.523 2 12Zm13.707-1.293a1 1 0 0 0-1.414-1.414L11 12.586l-1.793-1.793a1 1 0 0 0-1.414 1.414l2.5 2.5a1 1 0 0 0 1.414 0l4-4Z" />
                            </svg>
                            <span>{{ opt.text }}</span>
                          </li>
                        </ul>
                      </div>
                      <div>
                        <p class="font-semibold text-gray-500 dark:text-gray-400 mb-1">User answered:</p>
                        <div class="px-3 py-2 rounded border" :class="entry.response?.isCorrect
                          ? 'bg-green-50 border-green-200 text-green-700'
                          : 'bg-red-50 border-red-200 text-red-700'">
                          {{
                            Array.isArray(entry.response?.selectedOption)
                              ? entry.response.selectedOption.join(', ')
                              : entry.response?.selectedOption || 'No response'
                          }}
                        </div>
                      </div>
                    </div>

                  </template>

                  <!-- FREE TEXT -->
                  <template v-else-if="entry.test.type === 'Free Text'">
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p class="font-semibold text-gray-500 dark:text-gray-400 mb-1">Expected Criteria <span
                            v-if="entry.test.required_points"></span>:</p>
                        <ul class="pl-1 space-y-1 text-gray-600 dark:text-gray-300">
                          <li v-for="rule in entry.test.rules" :key="rule.word">
                            - {{ rule.condition }} <strong>{{ rule.word }}</strong> <span v-if="rule.points">({{
                              rule.points }} points)</span>
                          </li>
                        </ul>
                      </div>
                      <div>
                        <p class="font-semibold text-gray-500 dark:text-gray-400 mb-1">User answered:</p>
                        <div class="px-3 py-2 rounded border" :class="entry.response?.isCorrect
                          ? 'bg-green-50 border-green-200 text-green-700'
                          : 'bg-red-50 border-red-200 text-red-700'">
                          {{ entry.response?.freeTextAnswer || entry.response?.userSelections?.[0] || 'No response' }}
                        </div>
                      </div>
                    </div>

                  </template>

                  <template v-else-if="entry.test.type === 'Fill the Gaps'">
                    <p class="font-semibold text-gray-500 dark:text-gray-400 mb-3">Gaps Filled:</p>
                    <div class="space-y-2">
                      <template v-if="entry.response?.userSelections?.length">
                        <div v-for="(correct, index) in entry.test.question.match(/\[([^\]]+)\]/g) || []" :key="index"
                          class="grid grid-cols-2 gap-4 items-center px-4 py-2 rounded border bg-white dark:bg-gray-800 shadow-sm"
                          :class="entry.response?.userSelections?.[index] === correct.replace(/\[|\]/g, '')
                            ? 'border-green-300'
                            : 'border-red-300'">
                          <div class="text-sm text-gray-600 dark:text-gray-300">
                            <span class="font-semibold">Expected:</span>
                            {{ correct.replace(/\[|\]/g, '') }}
                          </div>
                          <div class="text-sm font-medium" :class="entry.response?.userSelections?.[index] === correct.replace(/\[|\]/g, '')
                            ? 'text-green-700'
                            : 'text-red-700'">
                            <span class="font-semibold">Answer:</span>
                            {{ entry.response?.userSelections?.[index] || 'No answer' }}
                          </div>
                        </div>
                      </template>
                      <p v-else class="text-gray-500">No response</p>
                    </div>
                  </template>

                  <!-- ORDERING -->
                  <template v-else-if="entry.test.type === 'Ordering'">
                    <p class="font-semibold text-gray-500 dark:text-gray-400 mb-1">User Order:</p>
                    <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 p-3 rounded border" :class="entry.response?.isCorrect
                      ? 'bg-green-50 border-green-200 text-green-700'
                      : 'bg-red-50 border-red-200 text-red-700'">
                      <div v-for="(item, i) in entry.response?.userOrder || []" :key="i"
                        class="flex items-center space-x-2 bg-white dark:bg-gray-800 shadow-sm rounded px-3 py-2 border border-gray-200 dark:border-gray-700">
                        <span class="bg-primary-600 text-white text-xs font-bold px-2 py-1 rounded-full">
                          {{ i + 1 }}
                        </span>
                        <span class="text-sm">{{ item.text || item }}</span>
                      </div>
                    </div>
                    <p v-if="!(entry.response?.userOrder?.length)" class="text-gray-500 mt-2">No response</p>


                  </template>


                  <!-- MATCH THE PAIRS -->
                  <template v-else-if="entry.test.type === 'Match the Pairs'">
                    <p class="font-semibold text-gray-500 dark:text-gray-400 mb-1">Matched Pairs:</p>
                    <div class="p-3 rounded border space-y-2" :class="entry.response?.isCorrect
                      ? 'bg-green-50 border-green-200 text-green-700'
                      : 'bg-red-50 border-red-200 text-red-700'">
                      <div v-for="(right, i) in entry.response?.userSelections || []" :key="i"
                        class="flex items-center justify-between bg-white dark:bg-gray-800 px-3 py-2 rounded shadow-sm border border-gray-200 dark:border-gray-700">
                        <span class="text-sm font-medium truncate w-1/2">
                          {{ entry.test.pairs?.[i]?.left || '–' }}
                        </span>
                        <span class="text-gray-400 mx-2">→</span>
                        <span class="text-sm font-medium truncate w-1/2 text-right">
                          {{ right || '–' }}
                        </span>
                      </div>
                    </div>
                    <p v-if="!(entry.response?.userSelections?.length)" class="text-gray-500 mt-2">No response</p>

                  </template>


                  <!-- FALLBACK -->
                  <template v-else>
                    <p class="text-sm text-gray-500">No display available for this question type.</p>
                  </template>
                </div>
              </div>
            </div>
          </div>

          <div v-else class="text-center py-10 text-gray-600 dark:text-gray-300">
            <p>No test data found for this user.</p>
          </div>
        </div>





      </div>
    </div>
  </div>
</template>


<script>
export default {
  data() {
    return {
      activeTab: 'unit',
    };
  },
  props: {
    visible: Boolean,
    units: {
      type: Object,
      default: () => ({
        contents: [],
        videos: [],
        audios: [],
        documents: [],
        iframes: [],
        scorms: [],
        assignments: [],
        tests: [],
      }),
    },
    tests: {
      type: Array,
      default: () => []
    },
    courseTitle: {
      type: String,
      default: 'Unknown Course',
    },
  },
  computed: {
    filteredCorrectOptions() {
      return (options) => options?.filter((o) => o.checked);
    },
    flattenedTestMap() {
      const result = [];

      for (const unitType in this.units) {
        this.units[unitType].forEach((unit) => {
          if (unit.tests?.length && unit.user_response?.length) {
            const groupedTests = unit.tests.map((test) => {
              const response = unit.user_response.find(
                (res) => res.input_key == test.id.toString()
              ) || null;

              return { test, response };
            });

            result.push({
              unitTitle: unit.title,
              tests: groupedTests,
            });
          }
        });
      }

      return result;
    },
    filteredUnits() {
      return Object.entries(this.units)
        .filter(([, unitList]) => unitList.length > 0)
        .reduce((filtered, [typeKey, unitList]) => {
          filtered[typeKey] = unitList;
          return filtered;
        }, {});
    },
    truncatedCourseTitle() {
      const maxLength = 50;
      return this.courseTitle.length > maxLength
        ? this.courseTitle.slice(0, maxLength) + "..."
        : this.courseTitle || "Unknown Course";
    },
  },
  methods: {
    tabButtonClass(tab) {
      return [
        'py-2 px-4 font-medium',
        this.activeTab === tab
          ? 'border-b-2 border-primary-700 text-primary-700'
          : 'text-gray-600 hover:text-primary-700'
      ];
    },
    truncateText(text, maxLength) {
      if (!text) return 'Test';
      return text.length > maxLength ? text.slice(0, maxLength) + '...' : text;
    },
    formatTime(seconds) {
      const hrs = Math.floor(seconds / 3600);
      const mins = Math.floor((seconds % 3600) / 60);
      const secs = Math.floor(seconds % 60);
      return `${hrs}h ${mins}m ${secs}s`;
    },
    closeModal() {
      this.activeTab = 'unit';
      this.$emit("close");
    },
  },
};
</script>

<style scoped>
.custom-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: #d1d5db #f3f4f6;
}

.custom-scrollbar::-webkit-scrollbar {
  width: 8px;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background-color: #d1d5db;
  /* Tailwind Gray-300 */
  border-radius: 4px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background-color: #9ca3af;
  /* Tailwind Gray-500 */
}

.custom-scrollbar::-webkit-scrollbar-track {
  background-color: #f3f4f6;
}
</style>
